import FilePicker from '@rio-cloud/rio-uikit/FilePicker';
import { FormattedMessage, useIntl } from 'react-intl';
import { ChangeEvent, useState } from 'react';
import Collapse from '@rio-cloud/rio-uikit/lib/es/Collapse';
import { VirtualList } from '@rio-cloud/rio-uikit/VirtualList';
import Button from '@rio-cloud/rio-uikit/Button';
import { Form } from 'react-router-dom';
import { useUploadVinListMutation } from '../../../../../services/connectivityInterruptionApi';
import { EActionType } from './columnDescriptorsLists';

export interface VinBlacklistModel {
    reason: string;
    listName: string;
    action: string;
    requester?: string;
    listNameVinImsi?: string;
    timestamp?: string;
}

interface VinItem {
    title: string;
    description: string;
}
interface VinBlacklist {
    file_name: string;
    reason: string;
}

export const InterruptionVinUpload = () => {
    const { formatMessage } = useIntl();
    const [expanderIsOpen, setExpanderIsOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileDataDisplay, setFileDataDisplay] = useState<VinItem[]>([]);
    const [formData, setFormData] = useState<VinBlacklist>(() => ({
        file_name: '',
        reason: '',
    }));
    const [errors, setErrors] = useState<{
        fileName?: string;
        reason?: string;
    }>({});

    const [uploadVinList] = useUploadVinListMutation();

    const displayData = (acceptedFiles: FileList | null) => {
        if (!acceptedFiles || acceptedFiles.length === 0) return;
        const file = acceptedFiles[0];
        setSelectedFile(file);
        setFormData((prev) => ({ ...prev, file_name: acceptedFiles![0].name }));

        const reader = new FileReader();
        reader.readAsText(acceptedFiles![0]);
        reader.onloadend = () => {
            const data = reader.result as string;
            let lines = data.split(/\r?\n/).filter((line) => line.trim());

            if (lines.length > 0 && lines[0].trim().toLowerCase() === 'vin') {
                lines = lines.slice(1);
            }

            const items = lines.flatMap((line) => line.split(/[,\t]/)).filter((item) => item.trim());

            const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/i;
            const invalids = items.filter((v) => !vinRegex.test(v.trim()));

            if (invalids.length > 0) {
                alert('Invalid file: contains non-VIN values.');
                return;
            }

            const formatted = items.map((item, index) => ({
                title: `Vin ${index + 1}`,
                description: item.trim(),
            }));
            setFileDataDisplay(formatted);
            setExpanderIsOpen(true);
        };
    };

    const onFileNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, file_name: value }));
    };

    const onReasonChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, reason: value }));
    };

    const validateForm = () => {
        const newErrors: { fileName?: string; reason?: string } = {};

        if (!formData.file_name) {
            newErrors.fileName = formatMessage({
                id: 'intl-msg:coreconAdminWeb.connectivityInterruption.filename.validation.missing',
            });
        }
        if (!formData.reason) {
            newErrors.reason = formatMessage({
                id: 'intl-msg:coreconAdminWeb.connectivityInterruption.reason.validation.missing',
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleUpload = async () => {
        const isValid = validateForm();
        if (!isValid || !selectedFile) {
            return;
        }

        try {
            await uploadVinList({
                file: selectedFile,
                vinBlacklistModel: {
                    reason: formData.reason,
                    listName: formData.file_name,
                    action: 'UPLOAD',
                },
            }).unwrap();

            alert('File uploaded successfully');
        } catch (err: any) {
            console.error('Upload failed', err);
            alert('Upload failed: ' + (err?.data || err?.error || 'Unknown error'));
        }
        setExpanderIsOpen(false);
        setFileDataDisplay([]);
    };

    return (
        <>
            <FilePicker
                className="btn-primary margin-bottom-20"
                multiple={false}
                accept={{
                    'text/csv': ['.csv'],
                }}
                label={formatMessage({ id: 'intl-msg:coreconAdminWeb.upload.selectFile' })}
                onPick={(acceptedFiles: FileList | null) => displayData(acceptedFiles)}
            />
            <Collapse open={expanderIsOpen}>
                <div className="panel-wrapper margin-top-20 margin-bottom-20">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="margin-bottom-15">
                                <VirtualList<VinItem>
                                    items={fileDataDisplay}
                                    className="max-height-400 padding-x-10"
                                    renderItem={(item, index) => (
                                        <div
                                            key={index}
                                            className="margin-bottom-10 padding-y-10 padding-x-15 bg-white
                                                     rounded shadow-accent text-color-darker"
                                        >
                                            <div className="text-medium" data-test-ignore="text">
                                                {item.title}
                                            </div>
                                            <div className="word-break" data-test-ignore="text">
                                                {item.description}
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Collapse>
            <Collapse open={expanderIsOpen}>
                <div className="panel-wrapper margin-top-20 margin-bottom-20">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="margin-bottom-15">
                                <div className={'form-group' + `${errors.fileName ? ' has-feedback has-error' : ''}`}>
                                    <label htmlFor="file_name">
                                        <span>
                                            <FormattedMessage
                                                id={'intl-msg:coreconAdminWeb.connectivityInterruption.filename'}
                                            />{' '}
                                            *
                                        </span>
                                    </label>
                                    <input
                                        className="form-control"
                                        id="file_name"
                                        value={formData.file_name}
                                        onChange={onFileNameChange}
                                    />
                                    {errors.fileName && (
                                        <span className="help-block margin-bottom-10">
                                            <span>{errors.fileName}</span>
                                        </span>
                                    )}
                                </div>
                                <div className={'form-group' + (errors.reason ? ' has-feedback has-error' : '')}>
                                    <label htmlFor="reason">
                                        <span>
                                            <FormattedMessage
                                                id={'intl-msg:coreconAdminWeb.connectivityInterruption.reason'}
                                            />{' '}
                                            *
                                        </span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        id="reason"
                                        value={formData.reason}
                                        tabIndex={3}
                                        onChange={onReasonChange}
                                    />
                                    {errors.reason && (
                                        <span className="help-block margin-bottom-10">
                                            <span>{errors.reason}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Collapse>
            {expanderIsOpen && (
                <div className="display-flex justify-content-end margin-bottom-20">
                    <Button type="button" className="btn btn-default" onClick={handleUpload}>
                        <FormattedMessage id={'intl-msg:coreconAdminWeb.upload.upload'} />
                    </Button>
                </div>
            )}
        </>
    );
};
